import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { type ImageSourcesDto } from '@pairy/next-pdf-viewer';

import { api } from '@/utils/api';
import { ApiErrorResponse, ApiResponse } from '@/types/api';
import { Nullable } from '@/types/helper';

interface GetFileRequest {
  fileId: number;
}

export interface GetFileResponse {
  file: {
    id: number;
    name: string;
    isBulk: boolean;
    isBlind: boolean;
    createdAt: string;
    editedAt: Nullable<string>;
    thumnail: string;
    hashId: Nullable<string>;
    pages: number;
    integration: { GTM: string | null; GA: string | null } | null;
    imageSources?: ImageSourcesDto;
  };
}

export const getFile = ({ fileId }: GetFileRequest) => {
  return api.get<ApiResponse<GetFileResponse>>(`/file`, {
    params: { fileId },
  });
};

const useFile = <
  TData = AxiosResponse<ApiResponse<GetFileResponse>>,
  TError = AxiosError<ApiErrorResponse>,
>(
  request: GetFileRequest,
  options?: UseQueryOptions<
    AxiosResponse<ApiResponse<GetFileResponse>>,
    TError,
    TData,
    ['getFile', GetFileRequest]
  >,
): UseQueryResult<TData, TError> => {
  return useQuery(['getFile', request], () => getFile(request), {
    ...options,
  });
};

export default useFile;
